/**
 * WO-D2.M — billing & plan. Shows license status; routes subscription management to the right
 * place per the license `manage` hint.
 *
 * In-app subscriptions (2026-06-19, design §5): management is PLATFORM-AWARE.
 *   - manage 'stripe_portal' → existing Stripe Billing Portal (openBillingPortal()).
 *   - manage 'app_store' / 'play_store' → RevenueCat Customer Center (presentCustomerCenter()).
 *   - On native, a "Restore purchases" action is always offered (Apple requirement).
 * The platform-split billing wrapper makes the RevenueCat calls safe no-ops on web.
 *
 * WO-M0 — built on the design system: Screen / Card / Badge / Button / Text + licenseBadge().
 * Locked-state recovery CTA uses variant="primary" (no accent — no owner-gated pricing here).
 */
import { useState } from 'react';
import { ActivityIndicator, Alert, Platform, StyleSheet, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { getLicense, openBillingPortal } from '../../../../src/api/account';
import { ApiRequestError } from '../../../../src/api/client';
import { openExternalUrl } from '../../../../src/platform/linking';
import {
  REVENUECAT_AVAILABLE,
  presentCustomerCenter,
  restorePurchases,
} from '../../../../src/billing/revenuecat';
import { Screen, Text, Card, Badge, Button, licenseBadge } from '../../../../src/components/ui';
import { theme } from '../../../../src/theme';

const IS_NATIVE = Platform.OS !== 'web';

export default function BillingScreen() {
  const license = useQuery({ queryKey: ['license'], queryFn: getLicense });
  const locked = license.data && !license.data.active;
  const manage = license.data?.manage ?? 'none';
  const isStore = manage === 'app_store' || manage === 'play_store';

  const [restoring, setRestoring] = useState(false);

  const licBadge = license.data ? licenseBadge(license.data.status ?? 'trial') : null;

  async function openStripePortal() {
    try {
      const { url } = await openBillingPortal();
      if (url) await openExternalUrl(url);
    } catch (e) {
      Alert.alert('Billing unavailable', e instanceof ApiRequestError ? e.message : 'Could not open billing.');
    }
  }

  async function manageSubscription() {
    // Store-billed (Apple/Google) → RevenueCat Customer Center; Stripe → portal.
    if (isStore && REVENUECAT_AVAILABLE) {
      await presentCustomerCenter();
      void license.refetch();
      return;
    }
    await openStripePortal();
  }

  async function handleRestore() {
    setRestoring(true);
    try {
      const { ok, subscribed } = await restorePurchases();
      if (!ok) {
        Alert.alert('Restore failed', 'Could not restore purchases. Please try again.');
        return;
      }
      void license.refetch();
      Alert.alert(
        subscribed ? 'Purchases restored' : 'Nothing to restore',
        subscribed
          ? 'Your subscription has been restored on this device.'
          : 'No active subscription was found for this store account.',
      );
    } finally {
      setRestoring(false);
    }
  }

  // Management CTA copy reflects where it routes.
  const manageTitle = locked
    ? isStore
      ? 'Manage subscription to restore access'
      : 'Update payment to restore access'
    : isStore
      ? 'Manage subscription'
      : 'Manage billing';

  return (
    <Screen scroll>
      <Text variant="title">Billing & plan</Text>

      {license.isLoading ? (
        <ActivityIndicator accessibilityLabel="Loading billing info" />
      ) : license.isError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            Could not load your billing details.
          </Text>
          <Button title="Retry" variant="ghost" onPress={() => void license.refetch()} />
        </Card>
      ) : (
        <Card>
          <View style={styles.planHeader}>
            <Text variant="subhead">{license.data?.plan ?? 'Subscription'}</Text>
            {licBadge ? <Badge status={licBadge.key} label={licBadge.label} /> : null}
          </View>

          {license.data?.seats ? (
            <Text variant="data" color="secondary">
              {license.data.seats} seats
            </Text>
          ) : null}

          {isStore ? (
            <Text variant="small" color="secondary">
              Billed through {manage === 'app_store' ? 'the App Store' : 'Google Play'}.
            </Text>
          ) : null}

          {locked ? (
            <View style={styles.lockBlock}>
              <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
                Your account is locked. {isStore ? 'Manage your subscription' : 'Update your payment'} to
                restore access.
              </Text>
            </View>
          ) : null}
        </Card>
      )}

      {/* Primary forward action — "primary" variant per hard rule (no accent in account screens) */}
      {!license.isError ? (
        <Button
          title={manageTitle}
          variant="primary"
          size="lg"
          onPress={() => void manageSubscription()}
          testID="billing-manage"
          accessibilityLabel={manageTitle}
        />
      ) : null}

      {/* Restore purchases — native only (Apple requirement); a no-op on web. */}
      {IS_NATIVE ? (
        <Button
          title="Restore purchases"
          variant="ghost"
          onPress={() => void handleRestore()}
          loading={restoring}
          disabled={restoring}
          testID="billing-restore"
          accessibilityLabel="Restore purchases"
        />
      ) : null}

      <Text variant="small" color="secondary" style={styles.fine}>
        {isStore
          ? 'Opens your store subscription settings to change plan or payment, or cancel.'
          : 'Opens your secure Stripe billing page to change plan, seats, or payment method.'}
      </Text>
    </Screen>
  );
}

const styles = StyleSheet.create({
  planHeader: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.md },
  lockBlock: { marginTop: theme.spacing.sm },
  fine: { textAlign: 'center' },
});
